import React, { useState } from 'react';
import { Eye, Pencil, Download, Trash2, Loader2 } from 'lucide-react';

import useDrive from '../../hooks/useDrive';
import {
  useViewDocument,
  useDeleteDocument,
} from '../../../UploadDocuments/hooks/Useuploaddocuments';

import { Actions, IconButton } from './Table.styles';
import RenameDocumentModal from '../../modals/RenameDocumentModal';

const INLINE_PREVIEW_EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg'];

const TableRowActions = ({ item }) => {
  const { setItems, setPreviewOpen, setPreviewFile, folderName, applicationId } = useDrive();

  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [busyAction, setBusyAction] = useState(null); // 'viewing' | 'downloading' | 'deleting'

  const isFolder = item.type === 'folder';

  const { mutate: viewDoc } = useViewDocument({
    onSuccess: (data, variables) => {
      const url = data?.documentUrl ?? data?.url;
      setBusyAction(null);

      if (variables.intent === 'download') {
        if (url) window.open(url, '_blank');
        return;
      }

      if (INLINE_PREVIEW_EXTENSIONS.includes(item.extension)) {
        setPreviewFile({ ...item, url });
        setPreviewOpen(true);
      } else if (url) {
        window.open(url, '_blank');
      }
    },
    onError: () => {
      setBusyAction(null);
      window.alert('Failed to open document. Please try again.');
    },
  });

  const { mutate: deleteDoc } = useDeleteDocument({
    onSuccess: (_, { documentId }) => {
      setBusyAction(null);
      setItems((prev) => prev.filter((x) => x.documentId !== documentId));
    },
    onError: () => {
      setBusyAction(null);
      window.alert('Failed to delete document. Please try again.');
    },
  });

  const handlePreview = () => {
    if (isFolder) return;
    setBusyAction('viewing');
    viewDoc({ documentId: item.documentId, applicationId, intent: 'view' });
  };

  const handleRename = () => {
    if (isFolder) return;
    setIsRenameOpen(true);
  };

  const handleDownload = () => {
    if (isFolder) return;
    setBusyAction('downloading');
    viewDoc({ documentId: item.documentId, applicationId, intent: 'download' });
  };

  const handleDelete = () => {
    if (isFolder) return;
    const confirmed = window.confirm(`Delete "${item.name}"?`);
    if (!confirmed) return;
    setBusyAction('deleting');
    deleteDoc({ documentId: item.documentId, applicationId });
  };

  const isBusy = busyAction !== null;

  return (
    <>
      <Actions>
        <IconButton onClick={handlePreview} disabled={isFolder || isBusy} title="View">
          {busyAction === 'viewing' ? <Loader2 size={16} className="spin" /> : <Eye size={16} />}
        </IconButton>

        <IconButton onClick={handleRename} disabled={isFolder || isBusy} title="Rename">
          <Pencil size={16} />
        </IconButton>

        <IconButton onClick={handleDownload} disabled={isFolder || isBusy} title="Download">
          {busyAction === 'downloading' ? (
            <Loader2 size={16} className="spin" />
          ) : (
            <Download size={16} />
          )}
        </IconButton>

        <IconButton $danger onClick={handleDelete} disabled={isFolder || isBusy} title="Delete">
          {busyAction === 'deleting' ? (
            <Loader2 size={16} className="spin" />
          ) : (
            <Trash2 size={16} />
          )}
        </IconButton>
      </Actions>

      <RenameDocumentModal
        isOpen={isRenameOpen}
        onClose={() => setIsRenameOpen(false)}
        doc={{ id: item.documentId, name: item.name }}
        applicationId={applicationId}
      />
    </>
  );
};

export default TableRowActions;
